import dayjs from 'dayjs';

import request from '../utils/Request';
import { Methods, NotificationType } from '../config';
import { downloadFile } from '../utils/download';
import addNotification from '../utils/addNotification';
import { ResponseType } from '../config/responetype';

const baseURL = 'company';

const companyService = {
  getCompanyById: (id, useDeleted = false) => {
    const query = useDeleted ? '?useDeleted=true' : '';
    return request({
      method: Methods.GET,
      resource: `${baseURL}/company/${encodeURIComponent(id)}${query}`,
    });
  },

  createCompanyContact: (companyId, data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/companyContact/${encodeURIComponent(companyId)}`,
      data,
    });
  },

  getCompanyBanks: (companyId, currencyCode) => {
    return request({
      method: Methods.GET,
      resource: `${baseURL}/company/${encodeURIComponent(companyId)}/banks?currencyCode=${encodeURIComponent(currencyCode)}`,
    });
  },

  getCompanies: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/companies`,
      data,
    });
  },

  createCompany: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}`,
      data,
    });
  },

  updateCompany: (data, id) => {
    return request({
      method: Methods.PUT,
      resource: `${baseURL}/${encodeURIComponent(id)}`,
      data,
    });
  },

  deleteCompany: (data, id) => {
    return request({
      method: Methods.DELETE,
      resource: `${baseURL}/${encodeURIComponent(id)}`,
      data,
    });
  },

  addContactToList: (data, id) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/companyContact/${encodeURIComponent(id)}`,
      data,
    });
  },

  deleteContactFromList: (data, id) => {
    return request({
      method: Methods.DELETE,
      resource: `${baseURL}/companyContact/${encodeURIComponent(id)}`,
      data,
    });
  },

  updateContact: (data, id) => {
    return request({
      method: Methods.PUT,
      resource: `${baseURL}/companyContact/${encodeURIComponent(id)}`,
      data,
    });
  },

  getExcelTemplate: async () => {
    const excelFromServer = await request({
      responseType: 'blob',
      resource: `${baseURL}/exceltemplate`,
    });
    return downloadFile(excelFromServer, `ceg_template-${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  },

  getExcelData: async () => {
    const excelFromServer = await request({
      responseType: 'blob',
      resource: `${baseURL}/exceldata`,
    });
    return downloadFile(excelFromServer, `ceg-${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  },

  importExcel: async (files, userId) => {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file);
    }
    formData.append('userId', userId);

    const resp = await request({
      method: Methods.POST,
      resource: `${baseURL}/excelimport`,
      formData: true,
      data: formData,
      responseType: 'blob',
    });

    if (resp?.type === ResponseType.xlsx) {
      addNotification({
        content: 'Az import sikertelen!',
        type: NotificationType.ERROR,
      });

      downloadFile(resp, `ERR_${files[0].name}`);
      return Promise.reject(new Error('Error in uploaded file!'));
    }

    const reader = new FileReader();
    reader.readAsText(resp);
    reader.onload = () => {
      const jsonData = JSON.parse(reader.result);
      if (jsonData?.error?.message) {
        return addNotification({
          content: jsonData.error.message,
          type: NotificationType.ERROR,
        });
      }
      return jsonData.data;
    };
  },

  uploadFile: (file, id, userId) => {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('userId', userId);

    return request({
      method: Methods.POST,
      resource: `${baseURL}/uploadFile/${encodeURIComponent(id)}`,
      formData: true,
      data: formData,
    });
  },

  deleteFile: (id, userId) => {
    return request({
      method: Methods.DELETE,
      resource: `${baseURL}/deleteFile/${encodeURIComponent(id)}`,
      data: { userId },
    });
  },

  getCompanyBankExcelTemplate: async () => {
    const excelFromServer = await request({
      responseType: 'blob',
      resource: `${baseURL}/companyBank/exceltemplate`,
    });
    return downloadFile(
      excelFromServer,
      `uzleti_partner_bankszamlaszam_template-${dayjs().format('YYYYMMDD_HHmm')}.xlsx`
    );
  },

  importBankExcel: async (files, userId) => {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file);
    }
    formData.append('userId', userId);

    const resp = await request({
      method: Methods.POST,
      resource: `${baseURL}/companyBank/excelimport`,
      formData: true,
      data: formData,
      responseType: 'blob',
    });

    if (resp?.type === ResponseType.xlsx) {
      addNotification({
        content: 'Az import sikertelen!',
        type: NotificationType.ERROR,
      });

      downloadFile(resp, `ERR_${files[0].name}`);
      return Promise.reject(new Error('Error in uploaded file!'));
    }

    const reader = new FileReader();
    reader.readAsText(resp);
    reader.onload = () => {
      const jsonData = JSON.parse(reader.result);
      if (jsonData?.error?.message) {
        return addNotification({
          content: jsonData.error.message,
          type: NotificationType.ERROR,
        });
      }
      return jsonData.data;
    };
  },

  searchCustomers: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/search/customer`,
      data,
    });
  },

  getSalesPersons: (companyId) => {
    return request({
      method: Methods.GET,
      resource: `${baseURL}/salesPersons/periods/${encodeURIComponent(companyId)}`,
    });
  },

  saveSalesPersons: (data) => {
    return request({
      method: Methods.PUT,
      resource: `${baseURL}/salesPersons/periods/`,
      data,
    });
  },
};

export default companyService;
